import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Eye, EyeOff } from "lucide-react";
import affiniaLogo from "@/assets/affinia-logo.png";
import affiniaBg from "@/assets/affinia-bg.png";
import { userLogin, markActive, startSSOLogin } from "@/services/userLogin";
import LoginCarousel from "@/components/LoginCarousel";
const Login = () => {
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState({ email: false, password: false });

  useEffect(() => {
    const ssoError = new URLSearchParams(window.location.search).get("sso_error");
    if (ssoError) {
      setError(ssoError);
      window.history.replaceState({}, "", window.location.pathname);
    }
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    try {
      const auth = await userLogin({ email, password });
      if (auth.user?.must_change_password) {
        navigate("/setup-password");
      } else {
        markActive();
        navigate("/dashboard");
      }
    } catch (error) {
      console.error('Login failed:', error);
      const errorMessage = error instanceof Error ? error.message : 'Invalid email or password.';
      setError(errorMessage);
      setTouched({ email: true, password: true });
    }
  };

  return (
    <div
      className="light min-h-dvh flex bg-cover bg-center bg-no-repeat text-foreground"
      style={{ backgroundImage: `url(${affiniaBg})` }}
    >
      {/* Left: login form */}
      <div className="flex items-center justify-center w-full px-4 py-8 sm:px-8 md:px-16 lg:w-[34%] lg:min-w-[32rem] lg:shrink-0 lg:px-8">
      <Card className="w-full max-w-md shadow-2xl bg-white/95 backdrop-blur-sm border-0">
        <CardHeader className="space-y-4 pb-6">
          <div className="flex justify-center">
            {/* The full-height logo is most of a phone screen on its own. */}
            <img src={affiniaLogo} alt="Affinia" className="h-24 sm:h-32 lg:h-40" />
          </div>
          <p className="text-center text-sm text-foreground">
            Sign in to your account
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <div className="bg-red-50 border border-red-300 text-red-700 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (error) setError("");
                }}
                onBlur={() => setTouched({ ...touched, email: true })}
                placeholder=""
                required
                className={error && touched.email ? "border-red-500 border-2" : "border-slate-400"}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (error) setError("");
                  }}
                  onBlur={() => setTouched({ ...touched, password: true })}
                  placeholder=""
                  required
                  className={error && touched.password ? "border-red-500 border-2" : "border-slate-400"}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button type="submit" className="w-full bg-primary hover:bg-primary/90">
              Login
            </Button>

            <div className="relative flex items-center gap-3 py-1">
              <div className="flex-1 border-t border-slate-300" />
              <span className="text-xs text-muted-foreground">or</span>
              <div className="flex-1 border-t border-slate-300" />
            </div>

            <Button type="button" onClick={startSSOLogin} variant="outline" className="w-full border-slate-400 text-foreground hover:bg-primary hover:text-white hover:border-primary active:bg-primary/90 transition-colors">
              Continue with SSO
            </Button>
          </form>
        </CardContent>
      </Card>
      </div>


      <div className="hidden lg:flex flex-1 items-center justify-center p-4 xl:p-6">
          <LoginCarousel />
      </div>
    </div>
  );
};

export default Login;
